<template>
  <ModalVue
    :modalActive="isThisModalVisible"
    @close="close"
    :hasHeaderBorder="true"
    :hasFooterBorder="true"
  >
    <Spinner
      v-if="isLoadingSubmit"
      color="primary"
      size="5rem"
      width="0.5rem"
      class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
    />

    <template #header>
      <ActionModalHeader :station-details="stationDetails" :title="modalTitle" />
    </template>

    <div v-if="!showShiftList" class="grid grid-cols-1 gap-6">
      <CoverageModeSelector
        v-if="scheduleSupports"
        v-model="coverageMode"
        :allow-unidentified="allowUnidentified"
        :unidentified-locked="unidentifiedLocked"
        :can-open-schedule-settings="canOpenScheduleSettings"
        @open-schedule-settings="openScheduleSettings(close)"
      />
      <CoverageResponsibleField
        v-if="coverageMode === CoverageMode.UNIDENTIFIED"
        v-model:responsible-id="coverageResponsibleId"
        v-model:note="coverageNote"
        :health-unit-id="props.stationDetails?.schedule?.healthUnit?.id ?? null"
        :health-unit-name="props.stationDetails?.schedule?.healthUnit?.name ?? null"
        :fallback-name="originCoverage.responsibleName"
        @update:responsible-name="selectedResponsibleName = $event"
      />

      <template v-if="isDoctorTarget">
        <v-autocomplete
          :model-value="selectedDoctor"
          @update:model-value="onDoctorSelected"
          :items="doctors"
          label="Médico"
          variant="outlined"
          color="primary20"
          density="comfortable"
          item-title="title"
          item-value="value"
          :loading="isLoadingDoctors"
          @update:search="handleSearchUpdate"
          clearable
          hide-details
          :no-data-text="isLoadingDoctors ? 'Carregando...' : 'Nenhum médico encontrado'"
          data-testid="management-doctor-select"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props" :title="item?.raw?.title" :subtitle="item?.raw?.subtitle" />
          </template>
        </v-autocomplete>

        <div class="flex flex-col md:flex-row gap-4">
          <v-autocomplete
            v-model="selectedFormation"
            :items="formations"
            label="Formação"
            variant="outlined"
            color="primary20"
            density="comfortable"
            item-title="title"
            item-value="value"
            :loading="isLoadingFormations"
            clearable
            hide-details
            no-data-text="Nenhuma formação encontrada"
          />

          <v-autocomplete
            v-model="selectedTeam"
            :items="teams"
            label="Equipe"
            variant="outlined"
            color="primary20"
            density="comfortable"
            item-title="title"
            item-value="value"
            :loading="isLoading"
            clearable
            hide-details
            no-data-text="Nenhuma equipe encontrada"
          />
        </div>

        <v-autocomplete
          v-model="selectedAllocator"
          :items="allocators"
          label="Médico escalado por"
          variant="outlined"
          color="primary20"
          density="comfortable"
          item-title="title"
          item-value="value"
          :loading="isLoadingAllocators"
          clearable
          hide-details
          no-data-text="Nenhum médico encontrado"
        />
      </template>

      <div v-if="coverageMode !== CoverageMode.OPEN">
        <p class="text-left text-gray-500 mb-4">Periodicidade</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-6">
          <SelectableCard
            v-for="opt in periodicity"
            :key="opt.value"
            :title="opt.title"
            :description="opt.description"
            :selected="selectedPeriodicity === opt.value"
            @click="selectedPeriodicity = opt.value"
            class="!flex"
          />
        </div>
      </div>

      <div class="mt-2 flex flex-col gap-4">
        <v-autocomplete
          v-model="selectedRecurrence"
          :items="recurrence"
          label="Recorrência da alocação"
          variant="outlined"
          color="primary20"
          density="comfortable"
          item-title="title"
          item-value="value"
          :loading="isLoading"
          clearable
          hide-details
          no-data-text="Nenhuma recorrência encontrada"
        />

        <p class="text-left text-sm text-[#68688D] -mt-3">{{ computedRecurrenceDescription }}</p>

        <OptionSelect
          v-if="selectedRecurrence === 'abcde'"
          label="Selecione as semanas do mês a serem fixadas"
          :options="weekOptions"
          v-model="selectedWeeks"
          multiple
        />

        <OptionSelect
          v-if="selectedRecurrence !== 'unique'"
          label="Selecione os dias da semana a serem alocados"
          :options="dayOptions"
          v-model="selectedDay"
          multiple
        />

        <v-row v-if="selectedRecurrence !== 'unique'">
          <div class="flex w-full gap-4">
            <div :class="selectedRepetition === 'personalized' ? 'w-3/4' : 'w-full'">
              <v-autocomplete
                v-model="selectedRepetition"
                :items="repetition"
                label="Se repete até"
                variant="outlined"
                color="primary20"
                density="comfortable"
                item-title="title"
                item-value="value"
                :loading="isLoading"
                hide-details
              />
            </div>

            <div v-if="selectedRepetition === 'personalized'" class="w-1/4">
              <DatePicker
                v-model="startDate"
                :max-date="
                  props.stationDetails?.schedule.endDate
                    ? new Date(props.stationDetails?.schedule.endDate)
                    : null
                "
                :initial-page="initialPage"
                locale="pt-BR"
                :masks="{ input: 'DD/MM/YYYY' }"
              >
                <template #default="{ inputValue, inputEvents }">
                  <v-text-field
                    label="Data final"
                    :model-value="inputValue"
                    v-on="inputEvents"
                    color="primary20"
                    variant="outlined"
                    density="comfortable"
                    :prepend-inner-icon="mdiCalendar"
                    readonly
                    hide-details
                  />
                </template>
              </DatePicker>
            </div>
          </div>
        </v-row>
      </div>
    </div>

    <div v-else>
      <div v-if="totalShiftsCount > 0" class="flex flex-col gap-4">
        <p class="text-sm text-left text-textPrimary" data-testid="management-doctor-summary">
          {{ summaryText }}
        </p>

        <CoveragePreviewSummary
          v-if="coveragePlan.hasContract"
          :plan="coveragePlan"
          :target-doctor-name="isDoctorTarget ? computedDoctorDetails.name : null"
        />

        <div class="flex flex-col md:flex-row gap-4">
          <CardDutyWithDoctor
            v-if="isDoctorTarget"
            :formation="computedDoctorDetails.formation"
            :team="computedDoctorDetails.team"
            :dutyLabel="
              selectedPeriodicity === 'COBERTURA'
                ? 'Cobertura'
                : selectedPeriodicity === 'FIXO'
                  ? 'Fixo'
                  : ''
            "
            :doctorName="computedDoctorDetails.name"
            :doctorCrm="computedDoctorDetails.crm"
            :active="true"
            :layout="'simple'"
            :npsScore="stationDetails?.doctorNpsScore?.npsScore"
            :avatarUrl="stationDetails?.doctorStation?.doctor?.user?.avatarUrl"
          />

          <div class="flex flex-col text-textPrimary">
            <p
              v-if="shouldShowInitialStationWarning"
              class="text-sm bg-warning/20 border border-warning rounded-md px-4 py-1 mb-4"
            >
              A alocação configurada não altera o plantão selecionado inicialmente<span
                class="bg-gray-200 rounded px-2 border border-gray-500 ml-4"
                >#{{ stationDetails?.doctorStation?.id }}</span
              >
            </p>
            <p
              v-if="hasConflictingStations"
              class="text-sm bg-negative/10 border border-negative rounded-md px-4 py-2 mb-4 flex items-center gap-2 text-[#DA1E28]"
            >
              <v-icon :icon="mdiAlertCircleOutline" :color="'#DA1E28'" class="w-5 h-5 shrink-0" />
              <span class="text-black"
                >Uma ou mais vagas selecionadas têm horários sobrepostos, ocorrendo conflito de
                alocação</span
              >
            </p>

            <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-left text-sm md:text-base">
              <div class="flex flex-col">
                <span class="font-semibold">Empresa</span>
                <span>{{ stationDetails?.schedule?.healthUnit?.company?.name || 'N/A' }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold">Escala</span>
                <span>{{ stationDetails?.schedule?.name || 'N/A' }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold">Unidade</span>
                <span>{{ stationDetails?.schedule?.healthUnit?.name || 'N/A' }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold">Posição</span>
                <span>{{ stationDetails?.position?.name || 'N/A' }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold">Turno</span>
                <span>{{ stationDetails?.shift?.name || 'N/A' }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold">Fuso horário</span>
                <span>{{ stationDetails?.timezone || 'N/A' }}</span>
              </div>
            </div>
          </div>
        </div>

        <ManagementDoctorTable
          :headers="previewHeaders"
          :items="limitedFormattedShifts"
          expandable
          @expand="handleExpandTable"
        />

        <ConsecutiveHoursBlockAlert :violations="consecutiveHoursViolations" :bulk="true" />
      </div>
      <div v-else class="flex flex-col items-center justify-center text-center py-10">
        <v-icon class="w-24 h-24 mb-4" :icon="mdiAlertCircleOutline" :color="'#DA1E28'" />
        <p class="text-base text-gray-600">
          Nenhum plantão encontrado com os parâmetros selecionados.
        </p>
      </div>
    </div>

    <template #footer>
      <div class="p-4">
        <template v-if="!showShiftList">
          <div class="flex flex-col-reverse md:flex-row gap-3 justify-between w-full">
            <OutlineButton height="48px" color="grey" @click="close" class="w-full md:w-auto">
              Cancelar
            </OutlineButton>
            <FilledButton
              height="48px"
              color="primary"
              @click="handleViewDoctorStations"
              :loading="isLoadingShifts"
              :disabled="!isViewShiftsButtonEnabled"
              class="w-full md:w-auto"
            >
              Ver plantões
            </FilledButton>
          </div>
        </template>

        <template v-else>
          <template v-if="totalShiftsCount > 0">
            <div class="flex flex-col-reverse md:flex-row gap-3 justify-between w-full">
              <OutlineButton
                height="48px"
                color="grey"
                @click="showShiftList = false"
                class="w-full md:w-auto"
              >
                Voltar
              </OutlineButton>
              <FilledButton
                height="48px"
                color="primary"
                @click="submitAllocation"
                :loading="isLoadingSubmit"
                :disabled="hasConsecutiveHoursViolations || isSubmitBlocked"
                class="w-full md:w-auto"
                data-testid="management-doctor-submit"
              >
                {{ submitLabel }}
              </FilledButton>
            </div>
          </template>
          <template v-else>
            <div class="flex justify-center w-full">
              <FilledButton
                height="48px"
                color="primary"
                @click="showShiftList = false"
                class="w-full md:w-auto"
              >
                Voltar
              </FilledButton>
            </div>
          </template>
        </template>
      </div>
    </template>
  </ModalVue>

  <ExpandedTableModal
    :showModal="isExpandedTableModalOpen"
    :headers="previewHeaders"
    :items="formattedShifts"
    :mode="mode === 'change' ? 'change' : 'allocate'"
    :title="modalTitle"
    :conflict-count="conflictShiftsCount"
    :open-count="coveragePlan.hasContract ? coveragePlan.counts.OPEN : openShiftsCount"
    :closed-count="coveragePlan.hasContract ? coveragePlan.counts.DOCTOR : closedShiftsCount"
    :unidentified-count="
      coveragePlan.hasContract ? coveragePlan.counts.UNIDENTIFIED : unregisteredShiftsCount
    "
    :blocked-count="coveragePlan.hasContract ? coveragePlan.counts.BLOCKED : null"
    :total-count="totalShiftsCount"
    @close="handleCloseExpandedTable"
  />

  <CoverageTransitionConfirmModal
    :show-modal="isTransitionModalOpen"
    :summary="transitionSummary"
    :context-label="transitionContextLabel"
    :responsible-name="selectedResponsibleName"
    :loading="isLoadingSubmit"
    @confirm="handleTransitionConfirm"
    @cancel="isTransitionModalOpen = false"
  />

  <!-- v-if: o aviso (com as ilustrações) só existe no DOM enquanto aberto. -->
  <PlaceholderDoctorBlockedModal
    v-if="placeholderBlocked.isOpen.value"
    v-model="placeholderBlocked.isOpen.value"
    :doctor-name="placeholderBlocked.doctorName.value"
    :allow-unidentified="allowUnidentified"
    @use-unidentified="onUseUnidentified"
  />
</template>

<script lang="ts" setup>
import { ref, computed, PropType, watch, toRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'
import { mdiAlertCircleOutline, mdiCalendar } from '@mdi/js'
import { format, endOfDay } from 'date-fns'

import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import DsPaymentFormationService from '@/Network/Services/DsPaymentFormationService'
import ScheduleUnitService from '@/Network/Services/ScheduleUnitService'
import StationService from '@/Network/Services/StationService'
import { ShiftDetails, StationFormSchedule } from '@/Network/Types/Responses/StationResponses'
import {
  ListStationsFromScheduleRequest,
  PatchStationsFromSchedulePayload,
  StationTypes
} from '@/Network/Types/Requests/StationRequests'

import ModalVue from '@/components/designSystem/modals/Modal.vue'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import CardDutyWithDoctor from '@/components/designSystem/Cards/CardDutyWithDoctor.vue'
import ManagementDoctorTable from '../../components/ManagementDoctorTable.vue'
import ExpandedTableModal from '../../components/ExpandedTableModal.vue'
import OptionSelect from '@/components/designSystem/Select/OptionSelect.vue'
import Spinner from '@/components/others/Spinner.vue'

import {
  dayMapping,
  dayOptions,
  periodicity,
  recurrence,
  recurrenceDescriptions,
  repetition,
  tableHeaders,
  weekMapping,
  weekOptions
} from '../utils/data'
import { useStationStore } from '@/pinia-store/useStationStore'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import { useExpandedTable, useInitialPage, useSummaryText } from '../utils/modalUtils'
import ActionModalHeader from '../../components/ActionModalHeader.vue'
import ConsecutiveHoursBlockAlert from '@/components/designSystem/Alerts/ConsecutiveHoursBlockAlert.vue'
import { useConsecutiveHoursCheck } from '@/hooks/useConsecutiveHoursCheck'
import type { ConsecutiveHoursCheckCandidate } from '@/Network/Types/Requests/StationRequests'
import {
  getPostStationApprovedMessage,
  isPostStationApprovedError
} from '@/modules/company/PostStation/errors'
import CoverageModeSelector from '@/components/Coverage/CoverageModeSelector.vue'
import { useUnidentifiedCoverageLock } from '@/components/Coverage/useUnidentifiedCoverageLock'
import CoverageResponsibleField from '@/components/Coverage/CoverageResponsibleField.vue'
import CoverageTransitionConfirmModal from '@/components/Coverage/CoverageTransitionConfirmModal.vue'
import CoveragePreviewSummary from '../../components/CoveragePreviewSummary.vue'
import { CoverageMode } from '@/Network/Types/Requests/CoverageRequests'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import {
  canMarkUnidentified,
  getStationCoverageInfo,
  scheduleSupportsCoverage
} from '@/utils/coverage'
import { getCoverageErrorMessage } from '@/utils/coverageErrors'
import PlaceholderDoctorBlockedModal from '@/components/Coverage/PlaceholderDoctorBlockedModal.vue'
import { usePlaceholderDoctorBlocked } from '@/composables/usePlaceholderDoctorBlocked'
import {
  findDoctorTitle,
  focusCoverageResponsibleSelect,
  focusFieldBySelector,
  isBlockedPlaceholderDoctor,
  reportPreventivePlaceholderAttempt
} from '@/utils/placeholderDoctor'
import {
  buildCoverageOperations,
  buildCoveragePatchStations,
  buildCoveragePlan,
  buildTransitionSummary,
  coverageTableHeaders,
  formatLegacyAvailability,
  needsTransitionConfirmation,
  toPreviewTableRow
} from '../utils/coverageAllocation'

let searchDebounceTimer: number | undefined = undefined

const props = defineProps({
  showModal: {
    type: Boolean,
    required: true
  },
  stationDetails: {
    type: Object as PropType<ShiftDetails | null>,
    default: () => null
  },
  mode: {
    type: String as PropType<'allocate' | 'change'>,
    required: true
  },
  // "Editar responsável" abre direto em "Ocupado sem identificação"; null = "Médico".
  initialCoverage: {
    type: String as PropType<CoverageMode | null>,
    default: null
  }
})

const emit = defineEmits(['closeModal', 'allocationUpdated'])

const toast = useToast()
const { notifyError } = useErrorToast()
const doctorStore = useDoctorStore()
const stationService = new StationService()
const { listDoctorsCompany } = storeToRefs(doctorStore)
const stationStore = useStationStore()

const isThisModalVisible = ref(props.showModal)
const isExpandedTableModalOpen = ref(false)
const showShiftList = ref(false)
const isLoadingDoctors = ref(false)
const isLoadingFormations = ref(false)
const isLoadingAllocators = ref(false)
const isLoadingShifts = ref(false)
const isLoadingSubmit = ref(false)

const doctors = ref<{ title: string; value: any; subtitle: string }[]>([])
const formations = ref<{ title: string; value: string | number }[]>([])
const teams = ref([
  { title: 'Interno', value: 'internal' },
  { title: 'Externo', value: 'external' }
])
const allocators = ref<{ title: string; value: string | number }[]>([])
const shifts = ref<StationFormSchedule[]>([])
const totalShiftsCount = ref(0)
const openShiftsCount = ref(0)
const closedShiftsCount = ref(0)
// `countUnregistered` do BE: ocupado sem identificação saiu de `countOpen` (null = BE antigo).
const unregisteredShiftsCount = ref<number | null>(null)
const conflictShiftsCount = ref(0)

const selectedDoctor = ref<string | null>(null)
const selectedFormation = ref<number | null>(null)
const selectedTeam = ref<string | null>(null)
const selectedAllocator = ref<number | null>(null)
const selectedPeriodicity = ref<string | null>('COBERTURA')
const selectedRecurrence = ref('unique')
const selectedRepetition = ref('end_scale')
const selectedWeeks = ref(['A', 'C'])
const selectedDay = ref(['seg'])
const startDate = ref()

const coverageMode = ref<CoverageMode>(CoverageMode.DOCTOR)
const coverageResponsibleId = ref<number | null>(null)
const coverageNote = ref('')
const selectedResponsibleName = ref<string | null>(null)
const isTransitionModalOpen = ref(false)

// Aviso do médico "coringa" (PLACEHOLDER_DOCTOR_BLOCKED): 422 do BE ou bloqueio preventivo.
const placeholderBlocked = usePlaceholderDoctorBlocked()
// Aviso aberto pela seleção no autocomplete: ao fechar ("Entendi"/ESC), o foco volta ao médico.
let focusDoctorOnClose = false
watch(placeholderBlocked.isOpen, (open) => {
  if (open || !focusDoctorOnClose) return
  focusDoctorOnClose = false
  void focusFieldBySelector('[data-testid="management-doctor-select"]')
})

// Seleção de médico: um "coringa" bloqueado nesta escala abre o aviso na hora e NÃO é aceito
// (o autocomplete é controlado, então o valor volta ao anterior).
function onDoctorSelected(value: string | null) {
  const schedule = props.stationDetails?.schedule
  if (isBlockedPlaceholderDoctor(schedule, value)) {
    placeholderBlocked.open(findDoctorTitle(doctors.value, value))
    focusDoctorOnClose = true
    reportPreventivePlaceholderAttempt(
      (payload) => stationService.reportPlaceholderAllocationAttempt(payload),
      {
        doctorId: Number(value),
        scheduleId: Number(schedule?.id),
        stationIds: props.stationDetails?.id ? [props.stationDetails.id] : undefined
      }
    )
    return
  }
  selectedDoctor.value = value
}

// "Usar Ocupado sem identificação": volta ao formulário já em UNIDENTIFIED, sem médico.
function onUseUnidentified() {
  // O primário leva o foco ao responsável (o campo de médico some com UNIDENTIFIED).
  focusDoctorOnClose = false
  coverageMode.value = CoverageMode.UNIDENTIFIED
  selectedDoctor.value = null
  showShiftList.value = false
  shifts.value = []
  void focusCoverageResponsibleSelect()
}

const scheduleSupports = computed(() => scheduleSupportsCoverage(props.stationDetails?.schedule))
const allowUnidentified = computed(() => canMarkUnidentified(props.stationDetails?.schedule))
const { unidentifiedLocked, canOpenScheduleSettings, openScheduleSettings } =
  useUnidentifiedCoverageLock(() => props.stationDetails?.schedule)
const originCoverage = computed(() => getStationCoverageInfo(props.stationDetails))
const isDoctorTarget = computed(() => coverageMode.value === CoverageMode.DOCTOR)
const coveragePlan = computed(() => buildCoveragePlan(shifts.value, coverageMode.value))
const transitionSummary = computed(() => buildTransitionSummary(coveragePlan.value))
const previewHeaders = computed(() =>
  coveragePlan.value.hasContract ? coverageTableHeaders : tableHeaders
)

const modalTitle = computed(() => {
  if (originCoverage.value.status === CoverageStatus.UNIDENTIFIED) {
    return coverageMode.value === CoverageMode.UNIDENTIFIED
      ? 'Editar responsável'
      : 'Identificar médico'
  }
  return props.mode === 'change' ? 'Alterar médico' : 'Alocar médico'
})

// Com contrato e nenhuma linha aplicável (todas bloqueadas) não há o que enviar (M6-C).
const isSubmitBlocked = computed(
  () => coveragePlan.value.hasContract && coveragePlan.value.applicableRows.length === 0
)

const pluralizeStations = (n: number) => `${n} ${n === 1 ? 'plantão' : 'plantões'}`

const submitLabel = computed(() => {
  // M4: com a prévia de cobertura o botão diz em quantos plantões vai aplicar.
  if (coveragePlan.value.hasContract) {
    const n = coveragePlan.value.applicableRows.length
    return n > 0 ? `Aplicar em ${pluralizeStations(n)}` : 'Aplicar'
  }
  if (isDoctorTarget.value) return 'Confirmar alocação'
  return needsTransitionConfirmation(coveragePlan.value) ? 'Continuar' : 'Confirmar'
})

const transitionContextLabel = computed(() => {
  const recurrenceTitle = recurrence.find((r) => r.value === selectedRecurrence.value)?.title
  const path = [
    props.stationDetails?.schedule?.healthUnit?.name,
    props.stationDetails?.schedule?.name,
    props.stationDetails?.position?.name
  ]
    .filter(Boolean)
    .join(' › ')
  return [recurrenceTitle, path].filter(Boolean).join(' · ')
})

// `initialCoverage` só vale se a escala permitir o modo pedido; senão abre em "Médico".
function resolveInitialCoverage(): CoverageMode {
  const requested = props.initialCoverage
  if (requested === CoverageMode.UNIDENTIFIED && allowUnidentified.value) return requested
  if (requested === CoverageMode.OPEN && scheduleSupports.value) return requested
  return CoverageMode.DOCTOR
}

const SUCCESS_MESSAGES: Record<CoverageMode, string> = {
  [CoverageMode.DOCTOR]: 'Alocação confirmada com sucesso!',
  [CoverageMode.UNIDENTIFIED]: 'Plantões marcados como ocupados sem identificação.',
  [CoverageMode.OPEN]: 'Vagas liberadas com sucesso.'
}

const {
  violations: consecutiveHoursViolations,
  hasViolations: hasConsecutiveHoursViolations,
  runCheck: runConsecutiveHoursCheck,
  applyErrorViolations: applyConsecutiveHoursError,
  clearViolations: clearConsecutiveHoursViolations
} = useConsecutiveHoursCheck()

watch([selectedDoctor, selectedTeam, shifts], () => clearConsecutiveHoursViolations())
watch(coverageMode, () => clearConsecutiveHoursViolations())

// Operação em massa: TODOS os plantões listados vão no mesmo lote — tudo-ou-nada.
function buildConsecutiveHoursCandidates(): ConsecutiveHoursCheckCandidate[] {
  const doctorId = selectedDoctor.value ? Number(selectedDoctor.value) : null
  const scheduleId = Number(props.stationDetails?.schedule?.id)
  if (!doctorId || !Number.isFinite(scheduleId)) return []

  // Com o contrato de cobertura, só os plantões que serão alterados entram na checagem:
  // linhas bloqueadas (IGNORED) não podem gerar falso positivo de horas consecutivas.
  const stations = coveragePlan.value.hasContract
    ? coveragePlan.value.applicableRows.map((row) => row.station)
    : shifts.value

  return stations.map((shift) => ({
    scheduleId,
    doctorId,
    stationId: shift.stationId,
    startHour: new Date(shift.startTime).toISOString(),
    endHour: new Date(shift.endTime).toISOString(),
    ourTeam: selectedTeam.value === 'internal',
    timezone: props.stationDetails?.timezone ?? '',
    excludeDoctorStationIds: shift.doctorStationId ? [shift.doctorStationId] : undefined
  }))
}

const { handleExpandTable, handleCloseExpandedTable } = useExpandedTable(
  isThisModalVisible,
  isExpandedTableModalOpen
)

const isLoading = computed(
  () => isLoadingDoctors.value || isLoadingFormations.value || isLoadingAllocators.value
)

const isViewShiftsButtonEnabled = computed(() => {
  if (coverageMode.value === CoverageMode.UNIDENTIFIED) return !!coverageResponsibleId.value
  if (coverageMode.value === CoverageMode.OPEN) return true
  return !!selectedDoctor.value && !!selectedTeam.value && !!selectedAllocator.value
})

const INDISPONIVEL = 'indisponível'

const computedDoctorDetails = computed(() => {
  const doctor = doctors.value.find((d) => d.value === selectedDoctor.value)
  const formation = formations.value.find((f) => f.value === selectedFormation.value)
  const team = teams.value.find((t) => t.value === selectedTeam.value)

  return {
    name: doctor ? doctor.title : 'Médico não selecionado',
    crm: doctor ? doctor.subtitle : INDISPONIVEL,
    formation: formation ? formation.title : INDISPONIVEL,
    team: team ? team.title : INDISPONIVEL
  }
})

const computedRecurrenceDescription = computed(() => {
  return (
    recurrenceDescriptions[selectedRecurrence.value as keyof typeof recurrenceDescriptions] || ''
  )
})

const initialPage = useInitialPage(toRef(props, 'stationDetails'))

const ALL_BLOCKED_MESSAGES: Record<CoverageMode, string> = {
  [CoverageMode.DOCTOR]:
    'Nenhum plantão pode ser alterado: todos os selecionados estão bloqueados.',
  [CoverageMode.UNIDENTIFIED]:
    'Nenhum plantão pode ser marcado como ocupado sem identificação: todos os selecionados estão bloqueados.',
  [CoverageMode.OPEN]:
    'Nenhum plantão pode ser liberado como vaga aberta: todos os selecionados estão bloqueados.'
}

const doctorNameToUse = computed(() => computedDoctorDetails.value.name)
const summaryText = computed(() => {
  if (!coveragePlan.value.hasContract) {
    return useSummaryText(doctorNameToUse, totalShiftsCount, 'management')
  }
  // Com contrato conta só as linhas aplicáveis (bloqueadas ficam de fora), como a prévia.
  const applicable = coveragePlan.value.applicableRows
  const n = applicable.length
  if (n === 0) return ALL_BLOCKED_MESSAGES[coverageMode.value]
  if (isDoctorTarget.value) return useSummaryText(doctorNameToUse, ref(n), 'management')
  if (coverageMode.value === CoverageMode.OPEN)
    return `Você está liberando ${pluralizeStations(n)} como vaga aberta. Deseja continuar?`
  // Já ocupado sem identificação + modo Ocupado = só troca o responsável ("Editar responsável").
  const swapping = applicable.filter((r) => r.status === CoverageStatus.UNIDENTIFIED).length
  const marking = n - swapping
  if (marking === 0)
    return `Você está trocando o responsável de ${pluralizeStations(swapping)}. Deseja continuar?`
  const markingText = `Você está marcando ${pluralizeStations(marking)} como ocupado sem identificação`
  return swapping > 0
    ? `${markingText} e trocando o responsável de ${pluralizeStations(swapping)}. Deseja continuar?`
    : `${markingText}. Deseja continuar?`
})

const formattedShifts = computed(() => {
  if (coveragePlan.value.hasContract) return coveragePlan.value.rows.map(toPreviewTableRow)
  return shifts.value.map((station) => ({
    id: `#${station.stationId}`,
    startDate: format(new Date(station.startDate), 'dd/MM/yyyy'),
    startTime: format(new Date(station.startTime), 'HH:mm'),
    endDate: format(new Date(station.endDate), 'dd/MM/yyyy'),
    endTime: format(new Date(station.endTime), 'HH:mm'),
    availability: formatLegacyAvailability(station),
    value: station.value ? `R$${station.value.toFixed(2).replace('.', ',')}` : INDISPONIVEL
  }))
})

const limitedFormattedShifts = computed(() => formattedShifts.value.slice(0, 3))

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
const buildDoctorStationsFilters = (): ListStationsFromScheduleRequest | null => {
  if (!props.stationDetails?.schedule?.id) return null
  if (isDoctorTarget.value && !selectedDoctor.value) return null
  // Backend com a feature recebe o alvo da cobertura para calcular bloqueios do preview.
  const coverageFilter = scheduleSupports.value ? { coverage: coverageMode.value } : {}

  if (selectedRecurrence.value === 'unique') {
    const filters: ListStationsFromScheduleRequest = {
      schedule: props.stationDetails.schedule.id,
      station: props.stationDetails.id
    }

    if (isDoctorTarget.value) filters.doctor = Number(selectedDoctor.value)
    return { ...filters, ...coverageFilter }
  }

  const sDate = new Date(props.stationDetails.startHour)
  let eDate: Date | undefined

  if (selectedRepetition.value === 'personalized' && startDate.value) {
    eDate = endOfDay(startDate.value)
  }

  if (selectedRepetition.value === 'end_scale' && props.stationDetails.schedule.endDate) {
    eDate = new Date(props.stationDetails.schedule.endDate)
  }

  const weekDays = selectedDay.value.map((day) => dayMapping[day]).join(',')

  let weekOfMonth = '0,1,2,3,4,5'

  let fortnightlyFilters = {}

  if (selectedRecurrence.value === 'fortnightly') {
    fortnightlyFilters = {
      alternatingWeek: true,
      stationWeekParity: props.stationDetails.stationWeekParity
    }
  }

  if (selectedRecurrence.value === 'abcde') {
    weekOfMonth = selectedWeeks.value.map((week) => weekMapping[week]).join(',')
  }

  const filters: ListStationsFromScheduleRequest = {
    schedule: props.stationDetails.schedule.id,
    position: props.stationDetails?.position?.id,
    shifts: props.stationDetails?.shift?.id,
    startDate: sDate.toISOString(),
    weekDays: weekDays,
    weekOfMonth: weekOfMonth,
    ...fortnightlyFilters
  }

  if (eDate) {
    filters.endDate = eDate.toISOString()
  }

  if (isDoctorTarget.value && selectedDoctor.value) {
    filters.doctor = Number(selectedDoctor.value)
  }

  return { ...filters, ...coverageFilter }
}

const shouldShowInitialStationWarning = computed(() => {
  if (!props.stationDetails?.id) return false
  return !shifts.value.some((shift) => shift.stationId === props.stationDetails?.id)
})

const hasConflictingStations = computed(() => {
  return conflictShiftsCount.value > 0
})

async function handleViewDoctorStations() {
  const baseFilters = buildDoctorStationsFilters()
  if (!baseFilters) {
    toast.error('Detalhes da escala não encontrados para buscar os plantões.')
    return
  }

  isLoadingShifts.value = true
  shifts.value = []
  try {
    const response = await stationService.listStationsFromSchedule({
      ...baseFilters,
      excludeHasCheckIn: true
    })

    shifts.value = response.stations
    totalShiftsCount.value = response.count
    openShiftsCount.value = response.countOpen
    closedShiftsCount.value = response.countClosed
    unregisteredShiftsCount.value = response.countUnregistered ?? null
    conflictShiftsCount.value = response.countConflict
    showShiftList.value = true
  } catch (error) {
    notifyError(error, 'Não foi possível carregar a lista de plantões')
    console.error(error)
  } finally {
    isLoadingShifts.value = false
  }
}

async function submitAllocation() {
  if (isDoctorTarget.value) {
    const isValidDoctor =
      !!selectedDoctor.value && doctors.value.some((d) => d.value === selectedDoctor.value)
    if (!isValidDoctor) {
      toast.error('Selecione um médico válido antes de confirmar a alocação.')
      return
    }
  } else if (coverageMode.value === CoverageMode.UNIDENTIFIED && !coverageResponsibleId.value) {
    toast.error('Selecione o responsável pela cobertura.')
    return
  }

  // M6: médico real → sem identificação (ou lote todo bloqueado) passa pela confirmação.
  if (needsTransitionConfirmation(coveragePlan.value)) {
    isTransitionModalOpen.value = true
    return
  }
  await executeAllocation()
}

async function handleTransitionConfirm() {
  // M6 só fecha quando o lote foi aplicado; em falha fica aberto junto do toast de erro.
  const succeeded = await executeAllocation()
  if (succeeded) isTransitionModalOpen.value = false
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
async function executeAllocation(): Promise<boolean> {
  isLoadingSubmit.value = true
  let succeeded = false
  try {
    const missingDoctorFields =
      isDoctorTarget.value &&
      (!selectedDoctor.value || !selectedTeam.value || !selectedAllocator.value)
    if (shifts.value.length === 0 || missingDoctorFields) {
      toast.error('Por favor, preencha todos os campos e visualize os plantões antes de confirmar.')
      return false
    }

    if (isDoctorTarget.value) {
      const canProceed = await runConsecutiveHoursCheck(buildConsecutiveHoursCandidates())
      if (!canProceed) {
        toast.error('Limite de horas consecutivas excedido. Nenhum plantão foi alterado.')
        return false
      }
    }

    const plan = coveragePlan.value
    const operations = buildCoverageOperations(plan, {
      fallbackOriginDoctorId: props.stationDetails?.doctorStation.doctor?.id ?? null,
      packageId: props.stationDetails?.doctorStation.package?.id ?? null
    })
    if (operations.length === 0) {
      toast.error('Nenhum plantão pode ser alterado com os parâmetros selecionados.')
      return false
    }

    const payload: PatchStationsFromSchedulePayload = {
      operations,
      patchStations: buildCoveragePatchStations({
        target: coverageMode.value,
        hasContract: plan.hasContract,
        doctorId: selectedDoctor.value ? Number(selectedDoctor.value) : null,
        ourTeam: selectedTeam.value === 'internal',
        userSponsorId: selectedAllocator.value,
        formationId: selectedFormation.value,
        stationType: selectedPeriodicity.value as StationTypes | null,
        responsibleId: coverageResponsibleId.value,
        note: coverageNote.value
      }),
      scheduleId: Number(props.stationDetails?.schedule.id)
    }

    await stationService.patchStationsFromSchedule(payload)

    toast.success(SUCCESS_MESSAGES[coverageMode.value])
    emit('allocationUpdated')
    close()
    succeeded = true
    await stationStore.getListStations()
  } catch (error) {
    console.error('Erro ao submeter a alocação:', error)
    handleAllocationError(error)
  } finally {
    isLoadingSubmit.value = false
  }
  return succeeded
}

function handleAllocationError(error: unknown): void {
  // Médico "coringa" (422 PLACEHOLDER_DOCTOR_BLOCKED): aviso descritivo em vez do toast.
  if (placeholderBlocked.handleError(error, findDoctorTitle(doctors.value, selectedDoctor.value))) {
    focusDoctorOnClose = false
    return
  }
  if (applyConsecutiveHoursError(error)) {
    toast.error('Limite de horas consecutivas excedido. Nenhum plantão foi alterado.')
    return
  }
  if (isPostStationApprovedError(error)) {
    // Defesa em profundidade (RN-07): a avaliação pode ter sido aprovada
    // enquanto o modal estava aberto — o backend recusa a mutação e devolve
    // o code fixo. Modal permanece aberto (sem close()), mesma decisão do
    // guard em update.station.modal.
    //
    // Sem sondagem aqui de propósito: este modal não tem estado de trava
    // preventiva (quem desabilita o acesso é o `stationDetailsModal`), então
    // usa só o helper de mensagem, não o composable.
    toast.error(getPostStationApprovedMessage(error))
    return
  }
  // Códigos de cobertura (Antecipe, check-in, flag desligada…) viram a frase do catálogo.
  const coverageMessage = getCoverageErrorMessage(error)
  if (coverageMessage) {
    toast.error(coverageMessage)
    return
  }
  // Antes: "Erro ao confirmar a alocação." estático.
  notifyError(error, 'Não foi possível confirmar a alocação')
}

const fetchDoctorsWithSearch = async (searchQuery: string) => {
  if (!props.stationDetails?.schedule?.healthUnit?.company?.id) return

  const companyId = props.stationDetails.schedule.healthUnit.company.id

  isLoadingDoctors.value = true
  try {
    await doctorStore.applyFilters(
      {
        inviteStatus: [
          CompanyDoctorInviteStatusEnum.ACCEPTED,
          CompanyDoctorInviteStatusEnum.PENDING
        ],
        companies: [String(companyId)],
        search: searchQuery
      },
      false
    )

    doctors.value = listDoctorsCompany.value.map((doc) => ({
      title: doc.title,
      value: doc.value,
      subtitle: doc.subtitle
    }))
  } catch (error) {
    notifyError(error, 'Não foi possível carregar a lista de médicos')
    console.error(error)
  } finally {
    isLoadingDoctors.value = false
  }
}

function handleSearchUpdate(search: string) {
  clearTimeout(searchDebounceTimer)
  isLoadingDoctors.value = true

  searchDebounceTimer = window.setTimeout(() => {
    fetchDoctorsWithSearch(search)
  }, 500)
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
const fetchData = async () => {
  if (
    !props.stationDetails?.schedule?.id ||
    !props.stationDetails?.schedule?.healthUnit?.company?.id
  ) {
    return
  }

  let defaultDay = 'seg'
  let defaultWeek = 'A'

  if (props.stationDetails && props.stationDetails.startHour) {
    try {
      const date = new Date(props.stationDetails.startHour)
      if (!isNaN(date.getTime())) {
        const dayIndex = date.getDay()
        const foundKey = (Object.keys(dayMapping) as Array<keyof typeof dayMapping>).find(
          (key) => dayMapping[key] === dayIndex
        )
        if (foundKey) {
          defaultDay = String(foundKey)
        }

        const dayOfMonth = date.getDate()
        const weekIndex = Math.floor((dayOfMonth - 1) / 7)

        const weekLetters = ['A', 'B', 'C', 'D', 'E']
        if (weekIndex >= 0 && weekIndex < weekLetters.length) {
          defaultWeek = weekLetters[weekIndex]
        }
      }
    } catch (e) {
      console.error('Erro ao calcular data padrão da station:', e)
    }
  }

  const scheduleId = props.stationDetails.schedule.id
  const companyId = props.stationDetails.schedule.healthUnit.company.id

  isLoadingDoctors.value = true
  try {
    await doctorStore.applyFilters(
      {
        inviteStatus: [
          CompanyDoctorInviteStatusEnum.ACCEPTED,
          CompanyDoctorInviteStatusEnum.PENDING
        ],
        companies: [String(companyId)],
        search: ''
      },
      false
    )
    doctors.value = listDoctorsCompany.value.map((doc) => ({
      title: doc.title,
      value: doc.value,
      subtitle: doc.subtitle
    }))
  } catch (error) {
    notifyError(error, 'Não foi possível carregar a lista de médicos')
    console.error(error)
  } finally {
    isLoadingDoctors.value = false
  }

  isLoadingFormations.value = true
  try {
    const formationRes = await new DsPaymentFormationService().getByScheduleId(scheduleId)
    formations.value = formationRes.data.map((item) => ({
      title: item.name,
      value: item.id
    }))
  } catch (error) {
    notifyError(error, 'Não foi possível carregar as formações')
    console.error(error)
  } finally {
    isLoadingFormations.value = false
  }

  isLoadingAllocators.value = true
  try {
    const allocatorRes = await new ScheduleUnitService().stationCreatingUsers(scheduleId)

    allocators.value = allocatorRes.map((user) => ({
      title: user.name,
      value: user.id
    }))
  } catch (error) {
    notifyError(error, 'Não foi possível carregar a lista de alocadores')
    console.error(error)
  } finally {
    isLoadingAllocators.value = false
  }

  if (props.mode === 'change' && props.stationDetails?.doctorStation) {
    const ds = props.stationDetails.doctorStation
    const incomingId = ds.doctor?.id ? String(ds.doctor.id) : null
    const isInList = !!incomingId && doctors.value.some((d) => d.value === incomingId)

    selectedDoctor.value = isInList ? incomingId : null

    if (incomingId && !isInList) {
      toast.warning(
        'Esta vaga não tem médico válido alocado. Selecione um médico para fazer a alocação.'
      )
    }

    selectedFormation.value = ds.dsPaymentDoctorFormation?.id || null
    selectedTeam.value = ds.ourTeam ? 'internal' : 'external'
    selectedAllocator.value = ds.userSponsor?.id || null
    selectedPeriodicity.value = ds.type
    selectedRecurrence.value = 'unique'
    selectedRepetition.value = 'end_scale'
    startDate.value = null
  }

  coverageMode.value = resolveInitialCoverage()
  coverageResponsibleId.value = null
  coverageNote.value = ''
  selectedResponsibleName.value = null
  if (coverageMode.value === CoverageMode.UNIDENTIFIED) {
    coverageResponsibleId.value = originCoverage.value.responsibleId
    coverageNote.value = originCoverage.value.note ?? ''
    selectedResponsibleName.value = originCoverage.value.responsibleName
  }

  selectedWeeks.value = [defaultWeek]
  selectedDay.value = [defaultDay]
}

function close() {
  emit('closeModal')
}

watch(
  () => props.stationDetails,
  (newDetails) => {
    if (newDetails && props.showModal) {
      fetchData()
    }
  },
  { immediate: true, deep: true }
)

watch(
  () => props.showModal,
  (newValue) => {
    isThisModalVisible.value = newValue
    showShiftList.value = false
    shifts.value = []
    totalShiftsCount.value = 0
    openShiftsCount.value = 0
    closedShiftsCount.value = 0
    conflictShiftsCount.value = 0
    isTransitionModalOpen.value = false
    if (!newValue) {
      isExpandedTableModalOpen.value = false
    } else if (props.stationDetails) {
      selectedRepetition.value = 'end_scale'
      fetchData()
    }
  }
)
</script>
